// src/components/core/MigrationModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect, useMemo, startTransition, useRef } from 'react';
import { Modal, Tabs, ConfigProvider } from 'antd';
import {
  DatabaseOutlined,
  ApiOutlined,
  CloudSyncOutlined,
  FileSyncOutlined,
} from '@ant-design/icons';
import { createRoot } from 'react-dom/client';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import marketplaceService from 'services/marketplaceService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import { TbDatabaseHeart } from 'react-icons/tb';

// Tabs
import ConnectionTab from './tabs/ConnectionTab';
import EnrichmentTab from './tabs/EnrichmentTab';
import MigratorTab from './tabs/MigratorTab';

// Sub-modal
import MigrationTokenFormModal from './MigrationTokenModal';

// *******************************************************************************
// THEME WRAPPER COMPONENT
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;

  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const MigrationModal = ({
  show,
  setShow,
  afterClose,
  onSuccess,
  // Enrichment visibility props (default true for backwards compatibility)
  showCnpjEnrichment = true,
  showCpfEnrichment = true,
  showProductsEnrichment = true,
}) => {
  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedModule = useZustand('module', 'config');
  const storedSubscription = useZustand('auth', 'subscription');
  const storedSubscriptions = useZustand('auth', 'subscriptions');
  const storedSetSubscription = useZustand('auth', 'setSubscription');

  // Get current module from Zustand store
  const currentModule = storedModule?.id;

  // Get migratory config from currentPage or use defaults
  const migratoryConfig = storedModule?.migratory || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const defaultTab = 'connection';
  const locked = false;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);

  // Token state
  const [tokenData, setTokenData] = useState(null);
  const [connectionEnabled, setConnectionEnabled] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);

  // Migration state
  const [migrationData, setMigrationData] = useState({
    enabled: false,
    autoMigrate: false,
    totalRecords: 0,
    migratedRecords: 0,
    updatedRecords: 0,
    skippedRecords: 0,
    lastMigratedAt: null,
  });
  const [isProcessing] = useState(false);
  const [isRefreshingCounts, setIsRefreshingCounts] = useState(false);

  // Enrichment state
  const [conflictStrategy, setConflictStrategy] = useState('ignore');
  const [enrichRecipientByCnpj, setEnrichRecipientByCnpj] = useState(false);
  const [enrichRecipientByCpf, setEnrichRecipientByCpf] = useState(false);
  const [enrichProducts, setEnrichProducts] = useState(false);
  const [enrichmentPrices, setEnrichmentPrices] = useState({
    recipientByCnpj: 1.5,
    recipientByCpf: 2.5,
    products: 0.5,
    entityByCnpj: 1.5,
    entityByCpf: 2.5,
  });
  const [cpfEnabled, setCpfEnabled] = useState(false);

  // Credits state
  const [creditsData, setCreditsData] = useState(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);

  // Track if initial load has completed to prevent storedSubscription updates from
  // overwriting fresh data from API polling
  const initialLoadCompleteRef = useRef(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Load existing token data from subscription on mount (ONLY on first load)
  useEffect(() => {
    if (!currentModule || !show) return;

    // Skip if initial load already completed - this prevents storedSubscription
    // updates (e.g., from refreshCreditsBalance) from overwriting fresh API data
    if (initialLoadCompleteRef.current) return;

    const migrationTokens = storedSubscription?.migrationTokens || [];
    const existingToken = migrationTokens.find(t => t.collectionName === currentModule);

    if (existingToken) {
      // Populate tokenData state with existing data
      setTokenData({
        tokenId: existingToken._id,
        tokenMasked: existingToken.tokenMasked,
        scope: existingToken.sourceInfo?.clientKey,
        sourceInfo: existingToken.sourceInfo,
        totalRecords: existingToken.totalRecords,
        isValidated: existingToken.isValidated,
      });

      // Set connection enabled state
      setConnectionEnabled(existingToken.isActive || false);

      // Populate migration data with existing progress
      setMigrationData({
        enabled: existingToken.isActive || false,
        autoMigrate: existingToken.autoMigrate || false,
        totalRecords: existingToken.totalRecords || 0,
        migratedRecords: existingToken.migratedRecords || 0,
        updatedRecords: existingToken.updatedRecords || 0,
        skippedRecords: existingToken.skippedRecords || 0,
        lastMigratedAt: existingToken.lastMigratedAt || null,
        delayBetweenRecordsMs: existingToken.delayBetweenRecordsMs || 20000,
      });

      // Load existing configuration
      setConflictStrategy(existingToken.conflictStrategy || 'ignore');
      // Use visibility props to determine which field to read:
      // - If products enrichment is hidden, we're in customers/suppliers context -> read enrichByCnpj/Cpf
      // - Otherwise, we're in invoices context -> read enrichRecipientByCnpj/Cpf
      if (!showProductsEnrichment) {
        // Customers/Suppliers context: use enrichByCnpj/Cpf fields
        // Fallback to legacy 'enrich' field if new fields are undefined (same as backend)
        const hasCnpjEnrich = existingToken.enrichByCnpj ?? existingToken.enrichByCNPJ;
        const hasCpfEnrich = existingToken.enrichByCpf;
        const hasLegacyEnrich = existingToken.enrich;
        // If new flags are undefined, use legacy 'enrich' as fallback for CNPJ
        const effectiveCnpjEnrich = hasCnpjEnrich !== undefined
          ? hasCnpjEnrich
          : (hasCpfEnrich === undefined ? (hasLegacyEnrich ?? false) : false);
        setEnrichRecipientByCnpj(effectiveCnpjEnrich);
        setEnrichRecipientByCpf(hasCpfEnrich ?? false);
      } else {
        // Invoices context: use enrichRecipientByCnpj/Cpf fields
        // Fallback to legacy 'enrichRecipient' or 'enrich' field if new fields are undefined
        const hasCnpjEnrich = existingToken.enrichRecipientByCnpj;
        const hasCpfEnrich = existingToken.enrichRecipientByCpf;
        const hasLegacyEnrich = existingToken.enrichRecipient ?? existingToken.enrich;
        // If new flags are undefined, use legacy 'enrichRecipient' as fallback for CNPJ
        const effectiveCnpjEnrich = hasCnpjEnrich !== undefined
          ? hasCnpjEnrich
          : (hasCpfEnrich === undefined ? (hasLegacyEnrich ?? false) : false);
        setEnrichRecipientByCnpj(effectiveCnpjEnrich);
        setEnrichRecipientByCpf(hasCpfEnrich ?? false);
      }
      // Products enrichment: use enrichByAi for products context, enrichProducts for invoices
      if (!showCnpjEnrichment) {
        // Products context: use enrichByAi
        setEnrichProducts(existingToken.enrichByAi ?? false);
      } else {
        // Invoices context: use enrichProducts
        setEnrichProducts(existingToken.enrichProducts ?? false);
      }

      // Mark initial load as complete
      initialLoadCompleteRef.current = true;
    } else {
      // Reset state when no token exists for current module
      setTokenData(null);
      setConnectionEnabled(false);
      setMigrationData({
        enabled: false,
        autoMigrate: false,
        totalRecords: 0,
        migratedRecords: 0,
        updatedRecords: 0,
        skippedRecords: 0,
        lastMigratedAt: null,
      });
      setConflictStrategy('ignore');
      setEnrichRecipientByCnpj(false);
      setEnrichRecipientByCpf(false);
      setEnrichProducts(false);
    }
  }, [storedSubscription, currentModule, show, showProductsEnrichment, showCnpjEnrichment]);

  // Fetch enrichment prices from API on mount
  useEffect(() => {
    if (!show) return;

    const fetchPrices = async () => {
      try {
        const [response] = await axiosService.get({
          url: API.MIGRATION.PRICES,
          returnAsObject: true,
        });

        if (response?.prices) {
          setEnrichmentPrices(response.prices);
        }
        if (response?.meta?.cpfEnabled !== undefined) {
          setCpfEnabled(response.meta.cpfEnabled);
        }
      } catch (error) {
        console.error('[MigrationModal] Error fetching prices:', error);
      }
    };

    fetchPrices();
  }, [show]);

  // Refresh counts from source database when modal opens or connection is enabled
  useEffect(() => {
    if (!tokenData?.tokenId || !connectionEnabled || !show) return;

    const refreshSourceCounts = async () => {
      setIsRefreshingCounts(true);

      try {
        const url = API.MIGRATION.QUEUE_REFRESH_COUNTS.replace(':tokenId', tokenData.tokenId);
        const [response] = await axiosService.post({
          url,
          returnAsObject: true,
        });

        if (response?.success && response?.totalRecords !== undefined) {
          setTokenData(prev => ({
            ...prev,
            totalRecords: response.totalRecords,
          }));
          setMigrationData(prev => ({
            ...prev,
            totalRecords: response.totalRecords,
          }));
        }
      } catch (error) {
        console.error('[MigrationModal] Error refreshing counts:', error);
      } finally {
        setIsRefreshingCounts(false);
      }
    };

    // Delay refresh to allow Switch animation to complete
    const timeoutId = setTimeout(() => {
      refreshSourceCounts();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [tokenData?.tokenId, connectionEnabled, show]);

  // Fetch migration status once when modal opens (to get lastMigratedAt, etc.)
  useEffect(() => {
    if (!tokenData?.tokenId || !connectionEnabled || !show) return;

    const fetchStatus = async () => {
      try {
        const url = API.MIGRATION.QUEUE_STATUS.replace(':tokenId', tokenData.tokenId);
        const [response] = await axiosService.get({
          url,
          returnAsObject: true,
        });

        if (response?.success && response?.progress) {
          setMigrationData(prev => ({
            ...prev,
            totalRecords: response.progress?.totalRecords ?? prev.totalRecords,
            migratedRecords: response.progress?.migratedRecords ?? prev.migratedRecords,
            updatedRecords: response.progress?.updatedRecords ?? prev.updatedRecords,
            skippedRecords: response.progress?.skippedRecords ?? prev.skippedRecords,
            lastMigratedAt: response.progress?.lastMigratedAt ?? prev.lastMigratedAt,
          }));
        }
      } catch (error) {
        console.error('[MigrationModal] Error fetching status:', error);
      }
    };

    // Delay to allow initial render
    const timeoutId = setTimeout(fetchStatus, 100);
    return () => clearTimeout(timeoutId);
  }, [tokenData?.tokenId, connectionEnabled, show]);

  // Check if enrichment is enabled (computed value)
  const hasEnrichmentEnabled = useMemo(() => {
    // Check based on visibility props and current enrichment state
    if (showCnpjEnrichment && enrichRecipientByCnpj) return true;
    if (showCpfEnrichment && enrichRecipientByCpf) return true;
    if (showProductsEnrichment && enrichProducts) return true;
    return false;
  }, [
    showCnpjEnrichment,
    showCpfEnrichment,
    showProductsEnrichment,
    enrichRecipientByCnpj,
    enrichRecipientByCpf,
    enrichProducts,
  ]);

  // Fetch credits data when enrichment is enabled and we have a token
  useEffect(() => {
    if (!tokenData?.tokenId || !connectionEnabled || !hasEnrichmentEnabled || !show) {
      setCreditsData(null);
      return;
    }

    const fetchCreditsData = async () => {
      setIsLoadingCredits(true);

      try {
        const url = API.MIGRATION.QUEUE_ESTIMATE_WITH_CREDITS.replace(
          ':tokenId',
          tokenData.tokenId
        );

        const [response] = await axiosService.get({
          url,
          returnAsObject: true,
        });

        if (response?.success) {
          setCreditsData(response);
        }
      } catch (error) {
        console.error('[MigrationModal] Error fetching credits data:', error);
      } finally {
        setIsLoadingCredits(false);
      }
    };

    // Delay to allow initial render
    const timeoutId = setTimeout(fetchCreditsData, 200);
    return () => clearTimeout(timeoutId);
  }, [tokenData?.tokenId, connectionEnabled, hasEnrichmentEnabled, show]);

  // Refresh credits data when enrichment settings change
  // Also disable autoMigrate if credits become insufficient
  useEffect(() => {
    if (!tokenData?.tokenId || !connectionEnabled || !hasEnrichmentEnabled || !show) {
      return;
    }

    const refreshCreditsData = async () => {
      setIsLoadingCredits(true);

      try {
        const url = API.MIGRATION.QUEUE_ESTIMATE_WITH_CREDITS.replace(
          ':tokenId',
          tokenData.tokenId
        );

        const [response] = await axiosService.get({
          url,
          returnAsObject: true,
        });

        if (response?.success) {
          startTransition(() => {
            setCreditsData(response);
          });

          // If autoMigrate is ON and credits are insufficient, disable it
          const recordsThatCanBeProcessed = response?.credits?.recordsThatCanBeProcessed || 0;
          const creditsRequired = response?.credits?.required || 0;
          const insufficientCredits = recordsThatCanBeProcessed === 0 && creditsRequired > 0;

          if (migrationData.autoMigrate && insufficientCredits) {
            // Disable autoMigrate via API
            const autoMigrateUrl = API.MIGRATION.QUEUE_AUTO_MIGRATE.replace(
              ':tokenId',
              tokenData.tokenId
            );
            const [autoMigrateResponse] = await axiosService.patch({
              url: autoMigrateUrl,
              data: { autoMigrate: false },
              returnAsObject: true,
            });

            if (autoMigrateResponse?.success) {
              startTransition(() => {
                setMigrationData(prev => ({ ...prev, autoMigrate: false }));
              });

              // Update Zustand store
              if (storedSubscription?.migrationTokens && storedSetSubscription) {
                const updatedTokens = storedSubscription.migrationTokens.map(token => {
                  if (token._id === tokenData.tokenId) {
                    return { ...token, autoMigrate: false };
                  }
                  return token;
                });

                const updatedSubscription = {
                  ...storedSubscription,
                  migrationTokens: updatedTokens,
                };

                const updatedSubscriptions = (storedSubscriptions || []).map(sub =>
                  sub._id === storedSubscription._id ? updatedSubscription : sub
                );

                storedSetSubscription(storedSubscription._id, updatedSubscriptions);
              }

              // Show notification
              appService.notification({
                flag: 'warning',
                title: 'auto_migrate_disabled',
                description: 'auto_migrate_disabled_insufficient_credits',
              });
            }
          }
        }
      } catch (error) {
        console.error('[MigrationModal] Error refreshing credits data:', error);
      } finally {
        setIsLoadingCredits(false);
      }
    };

    // Debounce refresh
    const timeoutId = setTimeout(refreshCreditsData, 500);
    return () => clearTimeout(timeoutId);
  }, [enrichRecipientByCnpj, enrichRecipientByCpf, enrichProducts]);

  // Track previous autoMigrate value to detect when it turns OFF
  const prevAutoMigrateRef = useRef(migrationData.autoMigrate);

  // Final fetch when autoMigrate changes from true to false
  // This ensures counters AND credits are updated even after polling stops
  useEffect(() => {
    const wasAutoMigrate = prevAutoMigrateRef.current;
    const isAutoMigrate = migrationData.autoMigrate;

    // Update ref for next comparison
    prevAutoMigrateRef.current = isAutoMigrate;

    // Only fetch when autoMigrate changes from true to false
    if (wasAutoMigrate && !isAutoMigrate && tokenData?.tokenId && show) {
      const fetchFinalStatus = async () => {
        try {
          // Fetch migration status (counters)
          const url = API.MIGRATION.QUEUE_STATUS.replace(':tokenId', tokenData.tokenId);
          const [response] = await axiosService.get({
            url,
            returnAsObject: true,
          });

          if (response?.success) {
            startTransition(() => {
              setMigrationData(prev => ({
                ...prev,
                totalRecords: response.progress?.totalRecords ?? prev.totalRecords,
                migratedRecords: response.progress?.migratedRecords ?? prev.migratedRecords,
                updatedRecords: response.progress?.updatedRecords ?? prev.updatedRecords,
                skippedRecords: response.progress?.skippedRecords ?? prev.skippedRecords,
                lastMigratedAt: response.progress?.lastMigratedAt ?? prev.lastMigratedAt,
              }));
            });
          }

          // Also fetch updated credits data if enrichment is enabled
          if (hasEnrichmentEnabled) {
            const creditsUrl = API.MIGRATION.QUEUE_ESTIMATE_WITH_CREDITS.replace(
              ':tokenId',
              tokenData.tokenId
            );
            const [creditsResponse] = await axiosService.get({
              url: creditsUrl,
              returnAsObject: true,
            });

            if (creditsResponse?.success) {
              startTransition(() => {
                setCreditsData(creditsResponse);
              });

              // Update Zustand store with new balance (for drawer widget)
              marketplaceService.refreshCreditsBalance();
            }
          }
        } catch (error) {
          console.error('[MigrationModal] Error fetching final status:', error);
        }
      };

      // Small delay to ensure backend has finished processing
      setTimeout(fetchFinalStatus, 500);
    }
  }, [migrationData.autoMigrate, tokenData?.tokenId, show, hasEnrichmentEnabled]);

  // Poll migration status every 5 seconds when autoMigrate is enabled
  useEffect(() => {
    if (!tokenData?.tokenId || !connectionEnabled || !migrationData.autoMigrate || !show) {
      return;
    }

    const pollStatus = async () => {
      try {
        // Fetch migration status
        const url = API.MIGRATION.QUEUE_STATUS.replace(':tokenId', tokenData.tokenId);
        const [response] = await axiosService.get({
          url,
          returnAsObject: true,
        });

        if (response?.success) {
          startTransition(() => {
            // IMPORTANT: Update autoMigrate from backend response
            // This ensures the switch turns OFF when backend disables autoMigrate
            // (e.g., when credits run out during migration)
            const backendAutoMigrate = response.autoMigrate ?? null;
            const autoMigrateChanged =
              backendAutoMigrate !== null && backendAutoMigrate !== migrationData.autoMigrate;

            setMigrationData(prev => ({
              ...prev,
              // Sync autoMigrate from backend (null means keep current value)
              autoMigrate: backendAutoMigrate !== null ? backendAutoMigrate : prev.autoMigrate,
              totalRecords: response.progress?.totalRecords ?? prev.totalRecords,
              migratedRecords: response.progress?.migratedRecords ?? prev.migratedRecords,
              updatedRecords: response.progress?.updatedRecords ?? prev.updatedRecords,
              skippedRecords: response.progress?.skippedRecords ?? prev.skippedRecords,
              lastMigratedAt: response.progress?.lastMigratedAt ?? prev.lastMigratedAt,
            }));

            if (response.progress?.totalRecords !== tokenData.totalRecords) {
              setTokenData(prev => ({
                ...prev,
                totalRecords: response.progress?.totalRecords,
              }));
            }

            // Also update Zustand store if autoMigrate changed (e.g., turned off by backend)
            if (
              autoMigrateChanged &&
              storedSubscription?.migrationTokens &&
              storedSetSubscription
            ) {
              const updatedTokens = storedSubscription.migrationTokens.map(token => {
                if (token._id === tokenData.tokenId) {
                  return {
                    ...token,
                    autoMigrate: backendAutoMigrate,
                  };
                }
                return token;
              });

              const updatedSubscription = {
                ...storedSubscription,
                migrationTokens: updatedTokens,
              };

              const updatedSubscriptions = (storedSubscriptions || []).map(sub =>
                sub._id === storedSubscription._id ? updatedSubscription : sub
              );

              storedSetSubscription(storedSubscription._id, updatedSubscriptions);
            }
          });
        }

        // Also refresh credits data if enrichment is enabled
        if (hasEnrichmentEnabled) {
          const creditsUrl = API.MIGRATION.QUEUE_ESTIMATE_WITH_CREDITS.replace(
            ':tokenId',
            tokenData.tokenId
          );
          const [creditsResponse] = await axiosService.get({
            url: creditsUrl,
            returnAsObject: true,
          });

          if (creditsResponse?.success) {
            startTransition(() => {
              setCreditsData(creditsResponse);
            });

            // Update Zustand store with new balance (for drawer widget)
            marketplaceService.refreshCreditsBalance();
          }
        }
      } catch (error) {
        console.error('[MigrationModal] Error polling status:', error);
      }
    };

    // Delay first poll to allow Switch animation to complete
    const initialTimeoutId = setTimeout(pollStatus, 500);
    const intervalId = setInterval(pollStatus, 5000);

    return () => {
      clearTimeout(initialTimeoutId);
      clearInterval(intervalId);
    };
  }, [
    tokenData?.tokenId,
    connectionEnabled,
    migrationData.autoMigrate,
    show,
    hasEnrichmentEnabled,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  // Helper function to update token settings in real-time
  const updateTokenSettings = useCallback(
    async updates => {
      if (!tokenData?.tokenId) return;

      try {
        const url = `${API.MIGRATION.UPDATE_TOKEN}/${tokenData.tokenId}`;
        const [response] = await axiosService.patch({
          url,
          data: updates,
          returnAsObject: true,
        });

        if (response?.success && storedSubscription?.migrationTokens && storedSetSubscription) {
          const updatedTokens = storedSubscription.migrationTokens.map(token => {
            if (token._id === tokenData.tokenId) {
              return {
                ...token,
                ...updates,
                totalRecords: migrationData.totalRecords,
                migratedRecords: migrationData.migratedRecords,
                updatedRecords: migrationData.updatedRecords,
                skippedRecords: migrationData.skippedRecords,
                lastMigratedAt: migrationData.lastMigratedAt,
              };
            }
            return token;
          });

          const updatedSubscription = {
            ...storedSubscription,
            migrationTokens: updatedTokens,
          };

          const updatedSubscriptions = (storedSubscriptions || []).map(sub =>
            sub._id === storedSubscription._id ? updatedSubscription : sub
          );

          storedSetSubscription(storedSubscription._id, updatedSubscriptions);
        }
      } catch (error) {
        console.error('[MigrationModal] Error updating token settings:', error);
      }
    },
    [
      tokenData?.tokenId,
      storedSubscription,
      storedSubscriptions,
      storedSetSubscription,
      migrationData,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (!loading) {
      setShow(false);
    }
  }, [loading, setShow]);

  const handleAfterClose = useCallback(() => {
    setActiveTab(defaultTab);
    setLoading(false);
    // Reset initial load flag so next open will load fresh data from subscription
    initialLoadCompleteRef.current = false;
    afterClose?.();
  }, [afterClose]);

  const handleTabChange = useCallback(key => {
    setActiveTab(key);
  }, []);

  // Connection handlers
  const handleOpenTokenModal = useCallback(() => {
    setShowTokenModal(true);
  }, []);

  const handleTokenModalSuccess = useCallback(
    data => {
      // Update local state with token data
      setTokenData({
        tokenId: data.tokenId,
        tokenMasked: data.tokenMasked,
        scope: data.scope,
        sourceInfo: data.sourceInfo,
        totalRecords: data.totalRecords,
        isValidated: true,
      });
      setShowTokenModal(false);

      if (data?.totalRecords) {
        setMigrationData(prev => ({
          ...prev,
          totalRecords: data.totalRecords,
        }));
      }

      // Update Zustand store with new token
      if (storedSubscription && storedSetSubscription) {
        const newToken = {
          _id: data.tokenId,
          collectionName: data.collectionName || currentModule,
          tokenMasked: data.tokenMasked,
          sourceInfo: data.sourceInfo,
          totalRecords: data.totalRecords || 0,
          migratedRecords: 0,
          updatedRecords: 0,
          skippedRecords: 0,
          isActive: false,
          isValidated: true,
          autoMigrate: false,
          conflictStrategy: 'ignore',
          enrichRecipientByCnpj: false,
          enrichRecipientByCpf: false,
          enrichProducts: false,
          enrichByAi: false, // For products module
          validatedAt: data.validatedAt,
        };

        const existingTokens = storedSubscription.migrationTokens || [];
        const updatedTokens = [...existingTokens, newToken];

        const updatedSubscription = {
          ...storedSubscription,
          migrationTokens: updatedTokens,
        };

        const updatedSubscriptions = (storedSubscriptions || []).map(sub =>
          sub._id === storedSubscription._id ? updatedSubscription : sub
        );

        storedSetSubscription(storedSubscription._id, updatedSubscriptions);
      }
    },
    [storedSubscription, storedSubscriptions, storedSetSubscription, currentModule]
  );

  const handleToggleConnection = useCallback(
    checked => {
      // Update local state immediately for smooth animation
      setConnectionEnabled(checked);

      // Wait for animation to complete (~200ms) then update via low-priority transition
      setTimeout(() => {
        startTransition(() => {
          updateTokenSettings({ isActive: checked });
        });
      }, 250);
    },
    [updateTokenSettings]
  );

  const handleDeleteToken = useCallback(() => {
    if (!tokenData?.tokenId) return;

    appService.modal({
      flag: 'confirm',
      title: 'remove_migration_token',
      content: 'remove_migration_token_confirm',
      okText: 'remove',
      cancelText: 'cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await appService.message({
            flag: 'loading',
            text: 'removing_token',
          });

          const url = API.MIGRATION.DELETE_TOKEN.replace(':tokenId', tokenData.tokenId);
          const [response] = await axiosService.delete({
            url,
            returnAsObject: true,
          });

          appService.messageDestroy();

          if (response?.success) {
            // Reset local state
            setTokenData(null);
            setConnectionEnabled(false);
            setMigrationData({
              enabled: false,
              autoMigrate: false,
              totalRecords: 0,
              migratedRecords: 0,
              updatedRecords: 0,
              skippedRecords: 0,
              lastMigratedAt: null,
            });
            setConflictStrategy('ignore');
            setEnrichRecipientByCnpj(false);
            setEnrichRecipientByCpf(false);
            setEnrichProducts(false);

            // Update Zustand store - remove token from migrationTokens array
            if (storedSubscription?.migrationTokens && storedSetSubscription) {
              const updatedTokens = storedSubscription.migrationTokens.filter(
                token => token._id !== tokenData.tokenId
              );

              const updatedSubscription = {
                ...storedSubscription,
                migrationTokens: updatedTokens,
              };

              const updatedSubscriptions = (storedSubscriptions || []).map(sub =>
                sub._id === storedSubscription._id ? updatedSubscription : sub
              );

              storedSetSubscription(storedSubscription._id, updatedSubscriptions);
            }

            await appService.message({
              flag: 's',
              text: 'token_removed_successfully',
              duration: 2,
            });
          } else {
            await appService.message({
              flag: 'e',
              text: 'token_removal_failed',
              duration: 3,
            });
          }
        } catch (error) {
          console.error('[MigrationModal] Error deleting token:', error);
          appService.messageDestroy();
          await appService.message({
            flag: 'e',
            text: 'token_removal_failed',
            duration: 3,
          });
        }
      },
    });
  }, [tokenData?.tokenId, storedSubscription, storedSubscriptions, storedSetSubscription]);

  // Enrichment handlers - using setTimeout + startTransition to prevent animation freezing
  const handleConflictStrategyChange = useCallback(
    strategy => {
      setConflictStrategy(strategy);
      setTimeout(() => {
        startTransition(() => {
          updateTokenSettings({ conflictStrategy: strategy });
        });
      }, 250);
    },
    [updateTokenSettings]
  );

  const handleEnrichRecipientByCnpjChange = useCallback(
    checked => {
      // Validate credits when enabling enrichment
      if (checked) {
        const creditsAvailable = creditsData?.credits?.available || 0;
        const perRecordCredits = creditsData?.credits?.perRecord || 0;
        // If we have credits data and user has zero credits for enrichment, block activation
        if (creditsData && perRecordCredits > 0 && creditsAvailable < perRecordCredits) {
          appService.notification({
            flag: 'warning',
            title: 'insufficient_credits',
            description: 'insufficient_credits_to_enable_enrichment',
          });
          return; // Don't enable enrichment
        }
      }

      setEnrichRecipientByCnpj(checked);
      setTimeout(() => {
        startTransition(() => {
          // Use visibility props to determine context:
          // - If products enrichment is hidden, we're in customers/suppliers context -> save enrichByCnpj
          // - Otherwise, we're in invoices context -> save enrichRecipientByCnpj
          if (!showProductsEnrichment) {
            updateTokenSettings({ enrichByCnpj: checked });
          } else {
            updateTokenSettings({ enrichRecipientByCnpj: checked });
          }
        });
      }, 250);
    },
    [updateTokenSettings, showProductsEnrichment, creditsData]
  );

  const handleEnrichRecipientByCpfChange = useCallback(
    checked => {
      // Validate credits when enabling enrichment
      if (checked) {
        const creditsAvailable = creditsData?.credits?.available || 0;
        const perRecordCredits = creditsData?.credits?.perRecord || 0;
        // If we have credits data and user has zero credits for enrichment, block activation
        if (creditsData && perRecordCredits > 0 && creditsAvailable < perRecordCredits) {
          appService.notification({
            flag: 'warning',
            title: 'insufficient_credits',
            description: 'insufficient_credits_to_enable_enrichment',
          });
          return; // Don't enable enrichment
        }
      }

      setEnrichRecipientByCpf(checked);
      setTimeout(() => {
        startTransition(() => {
          // Use visibility props to determine context:
          // - If products enrichment is hidden, we're in customers/suppliers context -> save enrichByCpf
          // - Otherwise, we're in invoices context -> save enrichRecipientByCpf
          if (!showProductsEnrichment) {
            updateTokenSettings({ enrichByCpf: checked });
          } else {
            updateTokenSettings({ enrichRecipientByCpf: checked });
          }
        });
      }, 250);
    },
    [updateTokenSettings, showProductsEnrichment, creditsData]
  );

  const handleEnrichProductsChange = useCallback(
    checked => {
      // Validate credits when enabling enrichment
      if (checked) {
        const creditsAvailable = creditsData?.credits?.available || 0;
        const perRecordCredits = creditsData?.credits?.perRecord || 0;
        // If we have credits data and user has zero credits for enrichment, block activation
        if (creditsData && perRecordCredits > 0 && creditsAvailable < perRecordCredits) {
          appService.notification({
            flag: 'warning',
            title: 'insufficient_credits',
            description: 'insufficient_credits_to_enable_enrichment',
          });
          return; // Don't enable enrichment
        }
      }

      setEnrichProducts(checked);
      setTimeout(() => {
        startTransition(() => {
          // Use visibility props to determine context:
          // - If CNPJ enrichment is hidden, we're in products context -> save enrichByAi
          // - Otherwise, we're in invoices context -> save enrichProducts
          if (!showCnpjEnrichment) {
            updateTokenSettings({ enrichByAi: checked });
          } else {
            updateTokenSettings({ enrichProducts: checked });
          }
        });
      }, 250);
    },
    [updateTokenSettings, showCnpjEnrichment, creditsData]
  );

  // Migrator handlers
  const handleToggleAutoMigrate = useCallback(
    checked => {
      // Update local state immediately for smooth animation
      setMigrationData(prev => ({ ...prev, autoMigrate: checked }));

      if (!tokenData?.tokenId) return;

      // Delay API call to allow Switch animation to complete
      setTimeout(async () => {
        try {
          const url = API.MIGRATION.QUEUE_AUTO_MIGRATE.replace(':tokenId', tokenData.tokenId);
          const [response] = await axiosService.patch({
            url,
            data: { autoMigrate: checked },
            returnAsObject: true,
          });

          if (response?.success && storedSubscription?.migrationTokens && storedSetSubscription) {
            // Use startTransition for Zustand store updates (non-urgent)
            startTransition(() => {
              const updatedTokens = storedSubscription.migrationTokens.map(token => {
                if (token._id === tokenData.tokenId) {
                  return {
                    ...token,
                    autoMigrate: checked,
                    totalRecords: migrationData.totalRecords,
                    migratedRecords: migrationData.migratedRecords,
                    updatedRecords: migrationData.updatedRecords,
                    skippedRecords: migrationData.skippedRecords,
                    lastMigratedAt: migrationData.lastMigratedAt,
                  };
                }
                return token;
              });

              const updatedSubscription = {
                ...storedSubscription,
                migrationTokens: updatedTokens,
              };

              const updatedSubscriptions = (storedSubscriptions || []).map(sub =>
                sub._id === storedSubscription._id ? updatedSubscription : sub
              );

              storedSetSubscription(storedSubscription._id, updatedSubscriptions);
            });
          } else {
            // Revert on failure
            setMigrationData(prev => ({ ...prev, autoMigrate: !checked }));

            // Show error notification to user based on error code
            const errorCode = response?.errorCode;
            if (errorCode === 'INSUFFICIENT_CREDITS') {
              appService.notification({
                flag: 'warning',
                title: 'auto_migrate_disabled',
                description: 'auto_migrate_disabled_insufficient_credits',
              });
            } else {
              // Generic error message
              appService.notification({
                flag: 'error',
                title: 'error',
                description: response?.message || 'auto_migrate_error',
              });
            }
          }
        } catch (error) {
          console.error('[MigrationModal] Error toggling autoMigrate:', error);
          setMigrationData(prev => ({ ...prev, autoMigrate: !checked }));
        }
      }, 250);
    },
    [
      tokenData?.tokenId,
      storedSubscription,
      storedSubscriptions,
      storedSetSubscription,
      migrationData,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. TAB ITEMS
  // ─────────────────────────────────────────────────────────────────────────────
  const hasToken = !!tokenData && connectionEnabled;

  const tabItems = [
    {
      key: 'connection',
      label: (
        <span>
          <ApiOutlined style={{ marginRight: 6 }} />
          {translateX('data_source')}
        </span>
      ),
      children: (
        <ConnectionTab
          tokenData={tokenData}
          connectionEnabled={connectionEnabled}
          locked={locked}
          onOpenTokenModal={handleOpenTokenModal}
          onToggleConnection={handleToggleConnection}
          onDeleteToken={handleDeleteToken}
        />
      ),
    },

    // Enrichment tab: only visible when token exists, disabled when connection is inactive
    tokenData && {
      key: 'enrichment',
      label: (
        <span>
          <FileSyncOutlined style={{ marginRight: 6 }} />
          {translateX('data_enrichment')}
        </span>
      ),
      disabled: !connectionEnabled,
      children: (
        <EnrichmentTab
          hasToken={hasToken}
          locked={locked}
          conflictStrategy={conflictStrategy}
          enrichRecipientByCnpj={enrichRecipientByCnpj}
          enrichRecipientByCpf={enrichRecipientByCpf}
          enrichProducts={enrichProducts}
          enrichmentPrices={enrichmentPrices}
          cpfEnabled={cpfEnabled}
          showCnpjEnrichment={showCnpjEnrichment}
          showCpfEnrichment={showCpfEnrichment}
          showProductsEnrichment={showProductsEnrichment}
          creditsData={creditsData}
          isLoadingCredits={isLoadingCredits}
          onConflictStrategyChange={handleConflictStrategyChange}
          onEnrichRecipientByCnpjChange={handleEnrichRecipientByCnpjChange}
          onEnrichRecipientByCpfChange={handleEnrichRecipientByCpfChange}
          onEnrichProductsChange={handleEnrichProductsChange}
        />
      ),
    },
    // Migrator tab: only visible when token exists, disabled when connection is inactive
    tokenData && {
      key: 'migrator',
      label: (
        <span>
          <CloudSyncOutlined style={{ marginRight: 6 }} />
          {translateX('migrator')}
        </span>
      ),
      disabled: !connectionEnabled,
      children: (
        <MigratorTab
          hasToken={hasToken}
          locked={locked}
          data={migrationData}
          isProcessing={isProcessing}
          isRefreshingCounts={isRefreshingCounts}
          onToggleAutoMigrate={handleToggleAutoMigrate}
          hasEnrichmentEnabled={hasEnrichmentEnabled}
          creditsData={creditsData}
          isLoadingCredits={isLoadingCredits}
        />
      ),
    },
  ].filter(Boolean);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Modal
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {migratoryConfig.icon || <DatabaseOutlined />}
            {translateX(migratoryConfig.title || 'migratory')}
          </span>
        }
        open={show}
        onCancel={handleClose}
        afterClose={handleAfterClose}
        destroyOnHidden
        width={560}
        footer={null}
        mask={{ closable: !loading }}
      >
        <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
      </Modal>

      {/* Migration Token Modal */}
      <MigrationTokenFormModal
        show={showTokenModal}
        setShow={setShowTokenModal}
        onSuccess={handleTokenModalSuccess}
        module={currentModule}
      />
    </>
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION (creates modal in React root)
// *******************************************************************************
const showMigrationModal = ({
  onSuccess,
  showCnpjEnrichment = true,
  showCpfEnrichment = true,
  showProductsEnrichment = true,
} = {}) => {
  // Create container
  const container = document.createElement('div');
  container.id = `migration-modal-container-${Date.now()}`;
  document.body.appendChild(container);

  // Create React root
  const root = createRoot(container);

  // Track modal open state
  let isModalOpen = true;

  // Render function to update modal state
  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <MigrationModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          onSuccess={onSuccess}
          showCnpjEnrichment={showCnpjEnrichment}
          showCpfEnrichment={showCpfEnrichment}
          showProductsEnrichment={showProductsEnrichment}
        />
      </ThemeWrapper>
    );
  };

  // Close handler: Sets show=false to trigger closing animation
  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  // AfterClose handler: Called after animation completes, then cleanup DOM
  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  // Initial render
  renderModal();
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default showMigrationModal;
export { MigrationModal };
